import React from 'react';
import PropTypes from 'prop-types';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import _map from 'lodash/map';
import _omit from 'lodash/omit';
import _reduce from 'lodash/reduce';
import _reject from 'lodash/reject';
import _set from 'lodash/set';
import _size from 'lodash/size';
import _some from 'lodash/some';
import _transform from 'lodash/transform';
// HOCs
import { compose } from 'redux';
import { connect } from 'react-redux';
import withPermissions from 'hocs/WithPermissions';
// Models
import { queGetCustomer as cpmQueGetCustomer } from 'models/cpm/customer';
import { queGetParking as cpmQueGetParking } from 'models/cpm/parking';
// Custom
import { SEARCHFIELD_TYPES } from 'constants/App';
import { DisabledContext } from 'components/misc/Contexts';
import {
  getDeviceLF, getDistributionLF, getInstallationCustomerLF, getInstallationLF, getSimcardLF, getProfileLF,
  cpmGetCarparkCustomerLF, cpmGetCarparkParkingLF,
} from 'actions';
import {
  customerDataShape,
  deviceDataShape,
  distributionDataShape,
  filterParamShape,
  listFeatureShape,
  installationDataShape,
  simcardDataShape,
  profileDataShape,
  cpmCustomerDataShape,
  cpmParkingDataShape,
} from 'constants/PropTypesShapes';
import { getHocDisplayName, invoke } from 'utils/Tools';

const wrapListFilters = (searchFields, resourcesGetterParams) => (WrappedComponent) => {
  class WrapListFilters extends React.Component {
    constructor(props) {
      super(props);

      this.handleReinit = this.handleReinit.bind(this);
      this.handleSwitch = this.handleSwitch.bind(this);
      this.handleFieldChange = this.handleFieldChange.bind(this);
      this.handleFilterFieldChange = this.handleFilterFieldChange.bind(this);
      this.handleFilterOperatorChange = this.handleFilterOperatorChange.bind(this);
      this.handleIsFieldDisplayable = this.handleIsFieldDisplayable.bind(this);

      // Build a reduced search fields array that reject excluded and not allowed fields
      const { excludedFields, hasPermission, listFeatures } = this.props;
      this.filteredSearchFields = _reject(searchFields || (listFeatures && listFeatures.searchFields), (sf) => {
        // Do not use hidden fields
        if (_get(sf, 'hidden', false)) {
          return true;
        }
        // Do not use excluded fields
        if (_includes(excludedFields, sf.field)) {
          return true;
        }
        // If the field needs a permission, check if connected user has it
        if (sf.permissions && !hasPermission(sf.permissions)) {
          return true;
        }
        return false;
      });

      // Remove from displayed search fields the independant ones
      this.displayedSearchFields = _reject(this.filteredSearchFields, ['isIndependant', true]);

      // Build an object with initial values for all fields
      this.initialFilters = _reduce(this.filteredSearchFields, (res, sf) => {
        _set(res, sf.field, {
          operator: sf.type.defaultOperator(sf.scope),
          value: sf.initial,
        });
        return res;
      }, {});

      // Initialize filters
      const { nbNotIndependantFilters, field, filters } = this.doInitialize();
      // More than one filter => switch to advanced mode
      const advanced = nbNotIndependantFilters > 1;

      this.state = {
        advanced,
        field,
        filters,
      };
    }

    componentDidUpdate(prevProps) {
      const {
        installation,
        device,
        customer,
        simcard,
        distribution,
        profile,
        cpmCustomer,
        cpmParking,
      } = this.props;
      const {
        installation: prevInstallation,
        device: prevDevice,
        customer: prevCustomer,
        simcard: prevSimcard,
        distribution: prevDistribution,
        profile: prevProfile,
        cpmCustomer: prevCpmCustomer,
        cpmParking: prevCpmParking,
      } = prevProps;

      const { resource: { defaultOperator } } = SEARCHFIELD_TYPES;

      // App
      // An installation is loaded
      if (installation && prevInstallation.loading && !installation.loading && installation.data) {
        invoke(() => {
          this.setState((state) => ({
            filters: {
              ...state.filters,
              installation: { operator: defaultOperator(), value: installation.data },
            },
          }));
        });
      }
      // A device is loaded
      if (device && prevDevice.loading && !device.loading && device.data) {
        invoke(() => {
          this.setState((state) => ({
            filters: {
              ...state.filters,
              device: { operator: defaultOperator(), value: device.data },
            },
          }));
        });
      }
      // An installation customer is loaded
      if (customer && prevCustomer.loading && !customer.loading && customer.data) {
        invoke(() => {
          this.setState((state) => ({
            filters: {
              ...state.filters,
              installationCustomer: { operator: defaultOperator(), value: customer.data },
            },
          }));
        });
      }
      // A simcard is loaded
      if (simcard && prevSimcard.loading && !simcard.loading && simcard.data) {
        invoke(() => {
          this.setState((state) => ({
            filters: {
              ...state.filters,
              simcard: { operator: defaultOperator(), value: simcard.data },
            },
          }));
        });
      }
      // A distribution is loaded
      if (distribution && prevDistribution.loading && !distribution.loading && distribution.data) {
        invoke(() => {
          this.setState((state) => ({
            filters: {
              ...state.filters,
              distribution: { operator: defaultOperator(), value: distribution.data },
            },
          }));
        });
      }
      // A profile is loaded
      if (profile && prevProfile.loading && !profile.loading && profile.data) {
        invoke(() => {
          this.setState((state) => ({
            filters: {
              ...state.filters,
              profile: { operator: defaultOperator(), value: profile.data },
            },
          }));
        });
      }

      // CPM
      // A carpark customer is loaded
      if (cpmCustomer && prevCpmCustomer.loading && !cpmCustomer.loading && cpmCustomer.data) {
        invoke(() => {
          this.setState((state) => ({
            filters: {
              ...state.filters,
              carparkCustomer: { operator: defaultOperator(), value: cpmCustomer.data },
            },
          }));
        });
      }
      // A carpark parking is loaded
      if (cpmParking && prevCpmParking.loading && !cpmParking.loading && cpmParking.data) {
        invoke(() => {
          this.setState((state) => ({
            filters: {
              ...state.filters,
              carparkParking: { operator: defaultOperator(), value: cpmParking.data },
            },
          }));
        });
      }
    }

    // Handlers
    handleReinit(keepIndependantFilters) {
      const independantFilters = keepIndependantFilters ? this.doGetIndependantFilters() : {};

      this.setState({
        field: this.filteredSearchFields[0].field,
        filters: {
          ...this.initialFilters,
          ...independantFilters,
        },
      }, this.doApplyFilters);
    }

    handleSwitch() {
      this.setState((state) => ({
        advanced: !state.advanced,
      }), () => {
        const { advanced } = this.state;
        // Switch to basic => reinit all filters
        if (!advanced) {
          this.handleReinit(true);
        }
      });
    }

    handleFieldChange(field) {
      this.setState({ field });
    }

    handleFilterFieldChange(field, value) {
      this.setState((state) => ({
        filters: {
          ...state.filters,
          ...{
            [field]: {
              operator: state.filters[field].operator,
              value,
            },
          },
        },
      }), this.doApplyFilters);
    }

    handleFilterOperatorChange(field, operator) {
      this.setState((state) => ({
        filters: {
          ...state.filters,
          ...{
            [field]: {
              operator,
              value: state.filters[field].value,
            },
          },
        },
      }), () => {
        // Apply filters only of field value is not undefined
        const { filters } = this.state;
        const searchField = _find(this.filteredSearchFields, ['field', field]);
        if (!searchField.isUndefined(filters[field].value)) {
          this.doApplyFilters();
        }
      });
    }

    handleIsFieldDisplayable(f) {
      const { advanced, field } = this.state;
      return ((advanced || field === f) && _some(this.filteredSearchFields, ['field', f]));
    }

    // Private
    doInitialize() {
      const {
        filtersParam, excludedFields,
        handleGetInstallation, handleGetDevice, handleGetInstallationCustomer,
        handleGetSimcard, handleGetDistribution, handleGetProfile,
        handleGetCpmCarparkCustomer, handleGetCpmCarparkParking,
      } = this.props;

      let nbNotIndependantFilters = 0;
      const filters = {};
      let { field } = this.filteredSearchFields[0];
      // To initialize filters, exclude from filter params the filters excluded
      const realFilterParams = _reject(filtersParam, (f) => (_includes(excludedFields, f.field)));
      // Parse all search fields
      _forEach(this.filteredSearchFields, (sf) => {
        // By default, set operator to search field default one
        let operator = sf.type.defaultOperator(sf.scope);
        // Try to find a filter applied for this field
        const fieldFilter = _find(realFilterParams, (filterParam) => {
          if (!sf.withOperators) {
            // No operators list: check field and operator
            return (filterParam.field === sf.filter.field && filterParam.operator === operator);
          }
          // With operators : check field only and set operator
          if (filterParam.field === sf.filter.field) {
            operator = filterParam.operator;
            return true;
          }
        });
        if (fieldFilter) {
          if (!sf.isIndependant) {
            field = sf.field;
            nbNotIndependantFilters += 1;
          }
          if (sf.needGetOnInit) {
            // A "get" on this resource is needed => set initial value and launch the get
            filters[sf.field] = {
              operator,
              value: sf.initial,
            };
            // Specific parameters for some getters
            const {
              getInstallationCustomerParams,
              cpmGetCarparkCustomerParams,
              cpmGetCarparkParkingParams,
            } = resourcesGetterParams || {};
            switch (sf.field) {
              case 'installation':
                handleGetInstallation(fieldFilter.value);
                break;
              case 'device':
                handleGetDevice(fieldFilter.value);
                break;
              case 'installationCustomer':
                handleGetInstallationCustomer(getInstallationCustomerParams.id(this.props), fieldFilter.value);
                break;
              case 'simcard':
                handleGetSimcard(fieldFilter.value);
                break;
              case 'distribution':
                handleGetDistribution(fieldFilter.value);
                break;
              case 'profile':
                handleGetProfile(fieldFilter.value);
                break;
              case 'carparkCustomer':
                handleGetCpmCarparkCustomer({
                  carparkId: cpmGetCarparkCustomerParams.id(this.props),
                  id: fieldFilter.value,
                });
                break;
              case 'carparkParking':
                handleGetCpmCarparkParking({
                  carparkId: cpmGetCarparkParkingParams.id(this.props),
                  id: fieldFilter.value,
                });
                break;
              default:
                break;
            }
          } else {
            // Set filter for this field
            filters[sf.field] = {
              operator,
              value: fieldFilter.value,
            };
          }
        } else {
          filters[sf.field] = {
            operator,
            value: sf.initial,
          };
        }
      });

      return { nbNotIndependantFilters, field, filters };
    }

    doGetIndependantFilters() {
      const { filters } = this.state;

      return _transform(filters, (result, value, key) => {
        const searchField = _find(this.filteredSearchFields, ['field', key]);
        if (searchField.isIndependant) {
          _set(result, key, value);
        }
      }, {});
    }

    doApplyFilters() {
      const { advanced, field, filters } = this.state;

      // Basic filters => empty all but current field filter
      if (!advanced) {
        // Parse filters to check if there are independant ones
        // Independant filters are always applied and never set 'field' attribute
        const independantFilters = this.doGetIndependantFilters();

        this.setState({
          filters: {
            ...this.initialFilters,
            ...{ [field]: filters[field] },
            ...independantFilters,
          },
        }, this.doLaunchApplyFilters);
      } else {
        this.doLaunchApplyFilters();
      }
    }

    doLaunchApplyFilters() {
      const { filters } = this.state;
      const { onApplyFilters } = this.props;

      const filtersToApply = _map(
        _filter(this.filteredSearchFields, (sf) => (
          !sf.isUndefined(filters[sf.field].value)
        )), (sf) => ({
          field: sf.filter.field,
          alias: sf.alias,
          operator: filters[sf.field].operator,
          value: sf.filter.key ? filters[sf.field].value[sf.filter.key] : filters[sf.field].value,
        }),
      );
      // Apply filters
      onApplyFilters(filtersToApply);
    }

    render() {
      const { disabled } = this.props;
      const { advanced, field, filters } = this.state;

      // Create props to be passed to wrapped component
      const compProps = _omit(this.props, [
        'excludedFields', 'filtersParam', 'onApplyFilters',
        'customer', 'device', 'distribution', 'installation', 'simcard',
        'handleGetDevice', 'handleGetDistribution', 'handleGetInstallation', 'handleGetInstallationCustomer', 'handleGetSimcard', 'handleGetProfile',
        'handleGetCpmCarparkCustomer', 'handleGetCpmCarparkParking',
        'hasPermission',
      ]);

      // Calculate the number of filters applied
      const nbFiltersApplied = _size(_filter(this.filteredSearchFields, (sf) => (!sf.isUndefined(filters[sf.field].value))));

      return (
        <DisabledContext.Provider value={disabled}>
          <WrappedComponent
            disabled={disabled}
            advanced={advanced}
            nbFiltersApplied={nbFiltersApplied}
            searchFields={this.displayedSearchFields}
            field={field}
            filters={filters}
            isFieldDisplayable={this.handleIsFieldDisplayable}
            onFieldChange={this.handleFieldChange}
            onFilterFieldChange={this.handleFilterFieldChange}
            onFilterOperatorChange={this.handleFilterOperatorChange}
            onReinit={() => this.handleReinit(false)}
            onSwitch={this.handleSwitch}
            {...compProps}
          />
        </DisabledContext.Provider>
      );
    }
  }

  WrapListFilters.propTypes = {
    disabled: PropTypes.bool,
    excludedFields: PropTypes.arrayOf(PropTypes.string).isRequired,
    filtersParam: PropTypes.arrayOf(filterParamShape).isRequired,
    listFeatures: listFeatureShape,
    onApplyFilters: PropTypes.func.isRequired,
    // From `withPermissions` HOC
    hasPermission: PropTypes.func.isRequired,
    // From `connect` HOC
    customer: PropTypes.shape({
      data: customerDataShape,
      loading: PropTypes.bool.isRequired,
    }),
    device: PropTypes.shape({
      data: deviceDataShape,
      loading: PropTypes.bool.isRequired,
    }),
    distribution: PropTypes.shape({
      data: distributionDataShape,
      loading: PropTypes.bool.isRequired,
    }),
    installation: PropTypes.shape({
      data: installationDataShape,
      loading: PropTypes.bool.isRequired,
    }),
    simcard: PropTypes.shape({
      data: simcardDataShape,
      loading: PropTypes.bool.isRequired,
    }),
    profile: PropTypes.shape({
      data: profileDataShape,
      loading: PropTypes.bool.isRequired,
    }),
    cpmCustomer: PropTypes.shape({
      data: cpmCustomerDataShape,
      loading: PropTypes.bool.isRequired,
    }),
    cpmParking: PropTypes.shape({
      data: cpmParkingDataShape,
      loading: PropTypes.bool.isRequired,
    }),
    handleGetDevice: PropTypes.func.isRequired,
    handleGetDistribution: PropTypes.func.isRequired,
    handleGetInstallation: PropTypes.func.isRequired,
    handleGetInstallationCustomer: PropTypes.func.isRequired,
    handleGetSimcard: PropTypes.func.isRequired,
    handleGetProfile: PropTypes.func.isRequired,
    handleGetCpmCarparkCustomer: PropTypes.func.isRequired,
    handleGetCpmCarparkParking: PropTypes.func.isRequired,
  };

  WrapListFilters.defaultProps = {
    disabled: false,
    customer: null,
    device: null,
    distribution: null,
    installation: null,
    simcard: null,
    profile: null,
    cpmCustomer: null,
    cpmParking: null,
    listFeatures: null,
  };

  WrapListFilters.displayName = getHocDisplayName('WrapListFilters', WrappedComponent);

  const mapStateToProps = (state) => ({
    // App
    customer: _get(state, 'listFilter.customer'),
    device: _get(state, 'listFilter.device'),
    distribution: _get(state, 'listFilter.distribution'),
    installation: _get(state, 'listFilter.installation'),
    simcard: _get(state, 'listFilter.simcard'),
    profile: _get(state, 'listFilter.profile'),
    // CPM
    cpmCustomer: _get(state, 'cpmListFilter.customer'),
    cpmParking: _get(state, 'cpmListFilter.parking'),
  });

  const mapDispatchToProps = (dispatch, ownProps) => ({
    // App
    handleGetDevice: (id) => {
      dispatch(getDeviceLF(id));
    },
    handleGetDistribution: (id) => {
      dispatch(getDistributionLF(id));
    },
    handleGetInstallation: (id) => {
      dispatch(getInstallationLF(id));
    },
    handleGetInstallationCustomer: (id, idCustomer) => {
      dispatch(getInstallationCustomerLF(id, idCustomer));
    },
    handleGetSimcard: (id) => {
      dispatch(getSimcardLF(id));
    },
    handleGetProfile: (id) => {
      dispatch(getProfileLF(id));
    },
    // CPM
    handleGetCpmCarparkCustomer: (data) => {
      dispatch(cpmGetCarparkCustomerLF(cpmQueGetCustomer(ownProps.hasPermission), data));
    },
    handleGetCpmCarparkParking: (data) => {
      dispatch(cpmGetCarparkParkingLF(cpmQueGetParking(ownProps.hasPermission), data));
    },
  });

  return compose(
    withPermissions,
    connect(
      mapStateToProps,
      mapDispatchToProps,
    ),
  )(WrapListFilters);
};

export default wrapListFilters;



// WEBPACK FOOTER //
// ./hocs/WrapListFilters.js